import Joi from 'joi-browser';
import React, { useState, useEffect, createRef, ReactNode, forwardRef } from 'react';
import Autosuggest from 'react-autosuggest';
import { useTranslation } from 'react-i18next';

import {
    addressAutoCompleteCheckoutFind,
    addressAutoCompleteCheckoutRetrieve,
} from '../../external/service/canadapostaddress.service';
import { TmAddress } from '../../tscapi/model/account';
import { Cookie } from '../../tscapi/utils/cookie';
import { withForm } from '../common/form';
import Input from '../common/input';
import Select from '../common/select';
import { getGuestEmail, hasGuestEmail } from '../signin/utils/sessionStorage';

interface DisplayFormProps {
    selectedAddress: TmAddress;
    setSelectedAddress: (TmAddress) => void;
    emailDataProp: { any };
    setEmailDataProp: (any) => void;
    setIsValid: (boolean) => void;
    handleSubmit: (any) => void;
    handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleChange: (any) => void;
    validate: (arg0: any, arg1: any) => any;
    validateProperty: (arg0: any, arg1: any) => any;
    setSchemaData: (any) => void;
    schema: { any };
    data: any;
    setData: (any) => void;
    setErrors: (any) => void;
    errors: any;
    province: { name: string; id: string }[];
    formState: string;
    touchedFields: Record<string, boolean>;
    editingFields: Record<string, boolean>;
    setErrorMessage?: (arg0: string) => void;
    isExistingGuest?: boolean;
}

export const AddressDisplay = ({
    selectedAddress,
    setSelectedAddress,
    emailDataProp,
    setEmailDataProp,
    setIsValid,
    validate,
    setErrors,
    validateProperty,
    handleSubmit,
    handleBlur,
    handleFocus,
    setData,
    data,
    errors,
    handleChange,
    setSchemaData,
    province,
    formState,
    touchedFields,
    editingFields,
    setErrorMessage,
    isExistingGuest,
}: DisplayFormProps) => {
    const [addressData, setAddressData] = useState(selectedAddress || {});
    const { t } = useTranslation();
    const formatPhoneNumber = (phoneNumberString) => {
        const phoneNumber = phoneNumberString;
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return '(' + match[1] + ') ' + match[2] + '-' + match[3];
        }
        return phoneNumber;
    };

    const formatPostalCode = (postalCode) => {
        const code = postalCode || '';

        return code.replace(/\W/g, '').replace(/(...)/, '$1 ');
    };

    useEffect(() => {
        if (selectedAddress && selectedAddress.DayPhone) {
            const formattedAddressData = {
                ...selectedAddress,
                DayPhone: formatPhoneNumber(selectedAddress.DayPhone),
                ZipCode: formatPostalCode(selectedAddress.ZipCode),
            };

            setAddressData(formattedAddressData);
        }
    }, [selectedAddress, editingFields]);

    const schema = {
        FirstName: Joi.string()
            .required()
            .regex(/^[a-z ,.'-]+$/i, { name: 'numbers' })
            .label('First name')
            .error((errors) => {
                if (errors[0].type == 'string.regex.name') {
                    return { message: t('Numbers and special characters are not allowed.') };
                }
                return {
                    message: t('First name is required.'),
                };
            }),
        LastName: Joi.string()
            .required()
            .regex(/^[a-z ,.'-]+$/i, { name: 'numbers' })
            .label('Last name')
            .error((errors) => {
                if (errors[0].type == 'string.regex.name') {
                    return { message: 'Numbers and special characters are not allowed.' };
                }
                return {
                    message: t('Last name is required.'),
                };
            }),
        DayPhone: Joi.string()
            .required()
            .min(14)
            .label('Phone number')
            .error((errors) => {
                if (errors[0].type == 'string.min') {
                    return { message: t('Please provide a valid phone number') };
                }
                return {
                    message: t('Phone number is required.'),
                };
            }),
        Street: Joi.string()
            .label('Address')
            .error((errors) => {
                return {
                    message: t('Address is required.'),
                };
            }),
        AddressRef1: Joi.string()
            .label('Address2')
            .allow('')
            .max(30)
            .error((errors) => {
                if (errors[0].type == 'string.max') {
                    return { message: 'Maximum 30 Characters Required' };
                }
                if (errors[0].type == 'string.min') {
                    return { message: 'Min 2 Characters Required' };
                }
                return {
                    message: 'Address2 is required.',
                };
            }),
        State: Joi.string()
            .label('Province')
            .invalid('0')
            .error((errors) => {
                return {
                    message: 'Province is required.',
                };
            }),
        ZipCode: Joi.string()
            .label('Postal code')
            .min(7)
            .error((errors) => {
                return {
                    message: t('Postal code is required.'),
                };
            }),
        City: Joi.string()
            .required()
            .label('City')
            .error((errors) => {
                return {
                    message: t('City is required.'),
                };
            }),
    };
    const [value, setAddressValue] = useState('');
    const [optOutSuggesion, setOptOutSuggestion] = useState(false);
    const [addressSuggestions, setAddressSuggestions] = useState([]);

    const cookie: Cookie = new Cookie();
    const expressCheckoutGuestEmail = cookie.TargetUrl === '/pages/expresscheckout' && hasGuestEmail();
    const optOutText: string =
        cookie.ApiLanguage.toLowerCase() == 'en'
            ? "I don't see my address. Let me type it in."
            : 'Je dois saisir mon adresse car elle n’apparaît pas.';

    const onAddressChange = (event, { newValue }) => {
        const errorsClone = { ...errors };
        const errorMessage = validateProperty({ name: 'Street', value: newValue }, schema);
        if (errorMessage) {
            errorsClone['Street'] = errorMessage;
        } else {
            delete errorsClone['Street'];
        }
        const dataClone = { ...data };
        dataClone['Street'] = newValue;
        setErrors(errorsClone);
        setAddressValue(newValue);
        setData(dataClone);
        setSelectedAddress(dataClone);
        setEmailDataProp(dataClone);
    };

    const findAddress = async (value, lastId) => {
        let streetAddresses: any[];

        try {
            const response = await addressAutoCompleteCheckoutFind(value, lastId);
            if (response && response.status == 200) {
                if (response.data.Items && response.data.Items.length > 0 && response.data.Items[0].Error) {
                    streetAddresses = [];
                    // streetAddresses.push({
                    //     Text: 'Sorry, we could not get your location',
                    //     Description: '',
                    //     Id: 'serviceUnavailable',
                    //     Next: '',
                    // });
                } else {
                    streetAddresses = response.data.Items;
                    streetAddresses.push({
                        Text: optOutText,
                        Description: '',
                        Id: 'optOut',
                        Next: '',
                    });
                }

                setAddressSuggestions(streetAddresses);
            }
        } catch (error) {
            console.log('error:' + error);
        }
    };

    const getAddressSuggestions = async (value) => {
        const lastId: string = undefined;
        if (!optOutSuggesion) {
            findAddress(value, lastId);
        }
    };

    const retrieveAddress = async (id) => {
        try {
            const response = await addressAutoCompleteCheckoutRetrieve(id);
            let selectedAddress;
            if (response && response.status == 200) {
                selectedAddress = response.data.Items[0];
                setAddressValue(selectedAddress.Line1);

                const dataClone = { ...data };
                dataClone['Street'] = selectedAddress.Line1;
                dataClone['AddressRef1'] = selectedAddress.Line2;
                dataClone['City'] = selectedAddress.City;
                dataClone['State'] = selectedAddress.Province;
                dataClone['ZipCode'] = selectedAddress.PostalCode;

                // manually force clear errors on fields set by CanPost
                const errorsClone = { ...errors };
                delete errorsClone['Street'];
                delete errorsClone['City'];
                delete errorsClone['State'];
                delete errorsClone['ZipCode'];
                setErrors(errorsClone || {});
                setData(dataClone);
            }
        } catch (error) {
            console.log('error:' + error);
        }
    };

    // When suggestion is clicked, Autosuggest needs to populate the input
    // based on the clicked suggestion. Teach Autosuggest how to calculate the
    // input value for every given suggestion.
    const getSuggestionValue = (suggestion) => {
        let selectedSuggestion = suggestion.Text + ' ' + suggestion.Description;
        if (suggestion.Id == 'optOut') {
            const dataClone = { ...data };
            dataClone['Street'] = '';
            dataClone['City'] = '';
            dataClone['State'] = '';
            dataClone['ZipCode'] = '';

            setData(dataClone);
            selectedSuggestion = value;
            setOptOutSuggestion(true);
        } else if (suggestion.Next == 'Retrieve') {
            setErrorMessage?.('');
            retrieveAddress(suggestion.Id);
            selectedSuggestion = value;
        } else if (suggestion.Next == 'Find') {
            const lastId = suggestion.Id;
            if (lastId) {
                findAddress(suggestion.Text, lastId);
                selectedSuggestion = value;
            }
        }
        return selectedSuggestion;
    };

    // Autosuggest will call this function every time you need to update suggestions.
    // You already implemented this logic above, so just use it.
    const onSuggestionsFetchRequested = ({ value }) => {
        getAddressSuggestions(value);
    };

    // Autosuggest will call this function every time you need to clear suggestions.
    const onSuggestionsClearRequested = () => {
        setAddressSuggestions([]);
    };

    // Autosuggest will pass through all these props to the input.
    const inputProps = {
        placeholder: 'Start typing your address...',
        value,
        onChange: onAddressChange,
    };
    const renderSuggestion = (suggestion) => {
        return <div>{suggestion.Text + ' ' + suggestion.Description}</div>;
    };
    function shouldRenderSuggestions(value) {
        return value.length >= 2;
    }

    const renderInputComponent = (inputProps) => (
        <Input name="address" required={true} label={t('Address')} error={errors.Street} {...inputProps} />
    );

    const handleSigninClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        cookie.TargetUrl = '/pages/expresscheckout';
        window.location.href = '/pages/signin';
    };

    const handleCheckboxOffers = ({ currentTarget: input }) => {
        const dataClone = { ...data };
        dataClone[input.name] = input.checked;
        setData(dataClone);
        setEmailDataProp(dataClone);
    };

    useEffect(() => {
        setAddressValue(selectedAddress.Street || '');
        setSchemaData(schema);
        setData(addressData);
    }, []);

    useEffect(() => {
        if (expressCheckoutGuestEmail) {
            const email = getGuestEmail()?.trim() || '';
            if (email) {
                setData((prev) => ({ ...prev, Email: email }));
                setEmailDataProp({ Email: email });
                setSelectedAddress((prev) => ({ ...prev, Email: email }));
            }
        }
    }, [expressCheckoutGuestEmail]);

    return (
        <form className="formWrap" noValidate>
            <span className="form__required">{t('Required fields are marked with an asterisk')} (*)</span>
            <div className="form__wrap">
                <div className="form__left">
                    {expressCheckoutGuestEmail && (
                        <div>
                            <div className="checkbox__signup--email-label">
                                <label htmlFor="Email">EMAIL:</label>
                                <div className="checkbox__signup--email-editwrap">
                                    <span className="checkbox__signup--email"> {getGuestEmail()} </span>
                                    <button
                                        type="button"
                                        className="checkbox__signup--edit"
                                        onClick={handleSigninClick}
                                        data-link-type="link"
                                        data-link-title="edit email address"
                                        data-component-type="express checkout"
                                    >
                                        Edit
                                    </button>
                                </div>
                            </div>
                            <span className="checkbox__signup--right">
                                We need this email to communicate to you about your order.
                            </span>
                            {!isExistingGuest && (
                                <div className="checkbox__signup-wrap" style={{ marginTop: '10px' }}>
                                    <input
                                        type="checkbox"
                                        id="Offers"
                                        name="Offers"
                                        value={data.Offers}
                                        checked={data.Offers || false}
                                        onChange={handleCheckboxOffers}
                                        style={{ display: 'none' }}
                                    />
                                    <label className="checkbox__signup-label" htmlFor="Offers">
                                        <div className="checkbox__signup--left">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                className="checkbox__signup--checked"
                                            >
                                                <path fill="none" d="M0 0h24v24H0V0z" />
                                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM17.99 9l-1.41-1.42-6.59 6.59-2.58-2.57-1.42 1.41 4 3.99z" />
                                            </svg>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                className="checkbox__signup--unchecked"
                                            >
                                                <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                                                <path d="M0 0h24v24H0z" fill="none" />
                                            </svg>
                                        </div>
                                        <div className="checkbox__signup--right">
                                            {t(
                                                "Sign me up for Special Offers & Today's Showstopper Emails. I understand that I may withdraw my consent at any time.",
                                            )}
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>
                    )}
                    <Input
                        name="FirstName"
                        value={data.FirstName}
                        required={true}
                        label={t('First Name')}
                        error={errors.FirstName}
                        onChange={handleChange}
                        data-qm-encrypt="true"
                    />
                    <Input
                        name="LastName"
                        value={data.LastName}
                        required={true}
                        label={t('Last Name')}
                        error={errors.LastName}
                        onChange={handleChange}
                        data-qm-encrypt="true"
                    />
                    <Input
                        name="DayPhone"
                        mask="phonenumber"
                        required={true}
                        value={data.DayPhone}
                        label={t('Phone number')}
                        error={
                            touchedFields.DayPhone && !editingFields?.DayPhone && errors.DayPhone ? errors.DayPhone : ''
                        }
                        onChange={handleChange}
                        data-qm-encrypt="true"
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                    />
                </div>
                <div className="form__right">
                    <Autosuggest
                        suggestions={addressSuggestions}
                        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                        onSuggestionsClearRequested={onSuggestionsClearRequested}
                        getSuggestionValue={getSuggestionValue}
                        renderSuggestion={renderSuggestion}
                        renderInputComponent={renderInputComponent}
                        inputProps={inputProps}
                        shouldRenderSuggestions={shouldRenderSuggestions}
                    />
                    <div className="form__address2wrap">
                        <Input
                            name="AddressRef1"
                            value={data.AddressRef1}
                            label={t('Address Line 2')}
                            error={errors.AddressRef1}
                            hint={t('e.g. Apartment, Suite, Unit, Building Floor')}
                            onChange={handleChange}
                            data-qm-encrypt="true"
                        />
                    </div>
                    <div className="form__citywrap">
                        <Input
                            name="City"
                            required={true}
                            value={data.City}
                            label={t('City')}
                            error={errors.City}
                            onChange={handleChange}
                            data-qm-encrypt="true"
                        />
                    </div>
                    <div className="form__provincewrap">
                        <Select
                            name="State"
                            required={true}
                            value={data.State}
                            label={t('Province')}
                            options={province}
                            error={errors.State}
                            onChange={handleChange}
                            data-qm-encrypt="true"
                        />
                    </div>
                    <div className="form__postalcodewrap">
                        <Input
                            name="ZipCode"
                            mask="postalcode"
                            value={data.ZipCode}
                            label={t('Postal code')}
                            required={true}
                            error={
                                touchedFields.ZipCode && !editingFields?.ZipCode && errors.ZipCode ? errors.ZipCode : ''
                            }
                            onChange={handleChange}
                            data-qm-encrypt="true"
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};
export const AddressForm = withForm(AddressDisplay);
